import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import SEOHead from "../components/shared/SEOHead.jsx";
import JsonLd from "../components/shared/JsonLd.jsx";
import { buildBreadcrumbJsonLd } from "../utils/jsonld.js";
import TurnstileWidget from "../components/shared/TurnstileWidget.jsx";
import { errorAria } from "../utils/formA11y.js";
import { API_BASE_URL } from "../utils/api.js";

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;
const DATENSCHUTZ_PATH = { de: "datenschutz", en: "privacy" };

const SUBJECT_OPTIONS = {
  de: [
    { value: "Allgemein", label: "Allgemein" },
    { value: "Event/Anmeldung", label: "Event / Anmeldung" },
    { value: "Freiplatz", label: "Freiplatz" },
    { value: "Ehrenamt", label: "Ehrenamt" },
    { value: "Trägerschaft", label: "Trägerschaft" },
    { value: "Sponsoring", label: "Sponsoring" },
    { value: "Presse", label: "Presse" },
  ],
  en: [
    { value: "General", label: "General" },
    { value: "Event/Registration", label: "Event / Registration" },
    { value: "Free ticket", label: "Free ticket" },
    { value: "Volunteering", label: "Volunteering" },
    { value: "Legal sponsorship", label: "Legal sponsorship" },
    { value: "Sponsoring", label: "Sponsoring" },
    { value: "Press", label: "Press" },
  ],
};

// Language-neutral hash token -> the localized `subject` option it
// preselects. A hash, not a query parameter: `?subject=Sponsoring` created
// four extra crawlable URLs that duplicate /kontakt's own title and copy,
// whereas a fragment never reaches the server and is ignored by every
// crawler (§ T-06). Add a token here when a page wants to deep-link into a
// specific subject.
const SUBJECT_BY_HASH = {
  sponsoring: { de: "Sponsoring", en: "Sponsoring" },
  traegerschaft: { de: "Trägerschaft", en: "Legal sponsorship" },
  freiplatz: { de: "Freiplatz", en: "Free ticket" },
  ehrenamt: { de: "Ehrenamt", en: "Volunteering" },
  presse: { de: "Presse", en: "Press" },
};

export const CONTACT_SUBJECT_HASH = Object.fromEntries(Object.keys(SUBJECT_BY_HASH).map((key) => [key, `#${key}`]));

const kontaktSchema = z.object({
  subject: z.string().min(1),
  name: z.string().min(1),
  email: z.string().email(),
  message: z.string().min(1),
  gdprConsent: z.boolean().refine((v) => v === true),
  honeypot: z.string().max(0), // must stay empty — bot trap, see §10.5
});

const fieldStyle = {
  fontFamily: "Inter, sans-serif",
  fontSize: "15px",
  color: "var(--color-ink)",
  background: "var(--color-cream)",
  border: "1px solid rgba(10,9,7,0.2)",
  borderRadius: "8px",
  padding: "12px 14px",
  width: "100%",
};

const labelStyle = { display: "flex", flexDirection: "column", gap: "6px", fontSize: "13px", color: "rgba(10,9,7,0.65)" };

const errorStyle = { fontSize: "12px", color: "#b3261e" };

const h2Style = { fontFamily: "var(--font-serif)", fontSize: "26px", color: "var(--color-cream)", margin: 0 };

export default function Kontakt({ lang }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState("idle");
  const [turnstileToken, setTurnstileToken] = useState("");
  const { hash } = useLocation();
  const path = lang === "de" ? "/kontakt" : "/contact";
  const altPath = lang === "de" ? "/contact" : "/kontakt";
  const subjectOptions = SUBJECT_OPTIONS[lang];
  const prefilledSubject = SUBJECT_BY_HASH[hash.replace(/^#/, "").toLowerCase()]?.[lang];
  const defaultSubject = subjectOptions.find((o) => o.value === prefilledSubject)?.value ?? subjectOptions[0].value;

  const metaTitle = lang === "de" ? "Kontakt: Fragen, Anmeldung, Presse" : "Contact: Questions, Registration, Press";
  const metaDescription =
    lang === "de"
      ? "So erreichst du Tales&Legends in Dresden: Kontaktformular, E-Mail, Telefon – für Fragen zu Events, Freiplätzen, Ehrenamt, Trägerschaft und Presse."
      : "How to reach Tales&Legends in Dresden: contact form, email, phone – for questions about events, free tickets, volunteering, legal sponsorship, and press.";
  const h1 = lang === "de" ? "Kontakt zu Tales&Legends" : "Contact Tales&Legends";

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: zodResolver(kontaktSchema), defaultValues: { subject: defaultSubject } });

  const handleVerify = useCallback((token) => setTurnstileToken(token), []);
  const handleExpire = useCallback(() => setTurnstileToken(""), []);

  const turnstilePending = Boolean(TURNSTILE_SITE_KEY) && !turnstileToken;

  async function onSubmit(values) {
    setStatus("sending");
    try {
      const { honeypot, ...rest } = values;
      const body = { ...rest, turnstileToken };
      const res = await fetch(`${API_BASE_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      setStatus(res.ok ? "sent" : "error");
    } catch {
      setStatus("error");
    }
  }

  return (
    <>
      <SEOHead lang={lang} path={path} altPath={altPath} title={metaTitle} description={metaDescription} />
      <JsonLd
        data={buildBreadcrumbJsonLd(
          [
            { name: t("nav.home"), path: "/" },
            { name: t("nav.kontakt"), path },
          ],
          lang
        )}
      />
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div className="tl-container" style={{ display: "flex", flexDirection: "column", gap: "48px", paddingTop: "64px", paddingBottom: "96px", maxWidth: "760px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            <p className="tl-eyebrow" style={{ margin: 0 }}>
              {t("kontakt.eyebrow")}
            </p>
            <h1 className="tl-hero-h1" style={{ fontFamily: "var(--font-serif)", fontSize: "44px", lineHeight: 1.1, color: "var(--color-cream)", margin: 0 }}>
              {h1}
            </h1>
            <p style={{ fontSize: "17px", lineHeight: 1.7, color: "var(--color-cream-soft)", margin: 0 }}>{t("kontakt.intro")}</p>
          </div>

          <div className="tl-card-light tl-form-card" style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <span style={{ fontFamily: "var(--font-serif)", fontSize: "22px", color: "var(--color-ink)" }}>
              {t("kontakt.formTitle")}
            </span>

            <form onSubmit={handleSubmit(onSubmit)} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
              <input type="text" tabIndex={-1} autoComplete="off" {...register("honeypot")} style={{ display: "none" }} />

              <label style={labelStyle}>
                {lang === "de" ? "Betreff" : "Subject"}
                <select style={fieldStyle} {...errorAria(errors, "subject")} {...register("subject")}>
                  {subjectOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {errors.subject && (
                  <span id="subject-error" role="alert" style={errorStyle}>
                    {lang === "de" ? "Pflichtfeld" : "Required field"}
                  </span>
                )}
              </label>

              <label style={labelStyle}>
                Name
                <input type="text" style={fieldStyle} {...errorAria(errors, "name")} {...register("name")} />
                {errors.name && (
                  <span id="name-error" role="alert" style={errorStyle}>
                    {lang === "de" ? "Pflichtfeld" : "Required field"}
                  </span>
                )}
              </label>

              <label style={labelStyle}>
                E-Mail
                <input type="email" style={fieldStyle} {...errorAria(errors, "email")} {...register("email")} />
                {errors.email && (
                  <span id="email-error" role="alert" style={errorStyle}>
                    {lang === "de" ? "Bitte gültige E-Mail angeben" : "Please enter a valid email address"}
                  </span>
                )}
              </label>

              <label style={labelStyle}>
                {lang === "de" ? "Nachricht" : "Message"}
                <textarea
                  style={{ ...fieldStyle, minHeight: "120px", resize: "vertical" }}
                  {...errorAria(errors, "message")}
                  {...register("message")}
                />
                {errors.message && (
                  <span id="message-error" role="alert" style={errorStyle}>
                    {lang === "de" ? "Pflichtfeld" : "Required field"}
                  </span>
                )}
              </label>

              <label style={{ display: "flex", alignItems: "flex-start", gap: "10px", fontSize: "12px", color: "rgba(10,9,7,0.6)" }}>
                <input
                  type="checkbox"
                  style={{ marginTop: "2px", accentColor: "var(--color-gold)" }}
                  {...errorAria(errors, "gdprConsent")}
                  {...register("gdprConsent")}
                />
                <span>
                  {t("kontakt.gdprConsentPrefix") + " "}
                  <Link to={`/${lang}/${DATENSCHUTZ_PATH[lang]}`} target="_blank" rel="noopener" style={{ color: "var(--color-ink)", textDecoration: "underline" }}>
                    {t("kontakt.gdprConsentLinkText")}
                  </Link>
                  {t("kontakt.gdprConsentSuffix")}
                </span>
              </label>
              {errors.gdprConsent && (
                <span id="gdprConsent-error" role="alert" style={errorStyle}>
                  {lang === "de" ? "Bitte bestätigen" : "Please confirm"}
                </span>
              )}

              {TURNSTILE_SITE_KEY && (
                <TurnstileWidget siteKey={TURNSTILE_SITE_KEY} onVerify={handleVerify} onExpire={handleExpire} />
              )}

              <button
                type="submit"
                className="tl-pill tl-pill-dark"
                style={{ padding: "14px 28px", alignSelf: "flex-start", opacity: status === "sending" ? 0.6 : 1 }}
                disabled={status === "sending" || turnstilePending}
              >
                {t("kontakt.sendCta")}
              </button>

              {status === "sent" && (
                <p role="status" style={{ fontSize: "14px", color: "var(--color-ink)" }}>
                  {t("kontakt.sentMessage")}
                </p>
              )}
              {status === "error" && (
                <p role="alert" style={errorStyle}>
                  {t("kontakt.errorMessage")}
                </p>
              )}
            </form>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <h2 style={h2Style}>{lang === "de" ? "Direkt erreichen" : "Reach us directly"}</h2>
            <div className="tl-card-light" style={{ display: "flex", flexDirection: "column", gap: "10px", padding: "28px 32px", fontSize: "15px", color: "rgba(10,9,7,0.8)" }}>
              <span>
                {"Reiboldschachtring 28, 01705 Freital"}
              </span>
              <span>
                <a href="mailto:hello@talesandlegends.org">hello@talesandlegends.org</a>
                {" · "}
                <a href="mailto:presse@talesandlegends.org">presse@talesandlegends.org</a>
              </span>
              <span>
                {t("kontakt.callPrefix") + " "}
                <a href="tel:+4935189672260" style={{ color: "var(--color-ink)", textDecoration: "underline" }}>
                  +49 351 8967226-0
                </a>
              </span>
              <span>
                <a href="https://discord.gg/6Xw4fycXBc" target="_blank" rel="noopener noreferrer">Discord</a>
                {" · "}
                <a href="https://www.instagram.com/tales.wtf" target="_blank" rel="noopener noreferrer">Instagram</a>
              </span>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <h2 style={h2Style}>{t("kontakt.whichChannelTitle")}</h2>
            <p style={{ fontSize: "16px", lineHeight: 1.75, color: "var(--color-cream-soft)", margin: 0 }}>
              {t("kontakt.whichChannelBody")}
            </p>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <h2 style={h2Style}>{t("kontakt.venueTitle")}</h2>
            <p style={{ fontSize: "16px", lineHeight: 1.75, color: "var(--color-cream-soft)", margin: 0 }}>
              {t("kontakt.venueBody")}
            </p>
            <Link to={`/${lang}/events`} style={{ fontSize: "15px", color: "var(--color-gold)", alignSelf: "flex-start" }}>
              {t("kontakt.venueCta")}
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
